import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, X } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import {
  DATA_SHEET_COL_TYPE,
  dataSheetsApi,
  type ColumnType,
  type DataSheetDetail,
} from "../api/dataSheetsApi";
import { HttpError } from "../api/http";
import { useCanEditSchema } from "../hooks/useCanEditSchema";
import { ConfirmModal } from "../components/ui/ConfirmModal";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: DataSheetDetail; columnTypes: ColumnType[] }
  | { status: "error"; message: string }
  | { status: "forbidden" };

interface Draft {
  name: string;
  description: string;
  /** Ordered filled slots only (we hide the 10 empty slots from the UI
   *  and re-assign slot numbers on save). Matches what the user
   *  actually sees in the form. */
  columns: { name: string; typeId: number }[];
}

const MAX_COLUMNS = 10;

/**
 * Editor for a data sheet's schema — its name, description, and up to
 * 10 named-typed columns. Reachable from the data sheets list via the
 * per-row "Schema" button. Routes and persists via dataSheetsApi.
 */
export function DataSheetSchemaPage() {
  const params = useParams<{ sheetId: string }>();
  const sheetId = Number(params.sheetId);
  const navigate = useNavigate();
  const canEditSchema = useCanEditSchema();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const reload = async () => {
    setState({ status: "loading" });
    try {
      const [detail, types] = await Promise.all([
        dataSheetsApi.get(sheetId),
        dataSheetsApi.listColumnTypes(),
      ]);
      setState({
        status: "ready",
        detail,
        columnTypes: types.items ?? [],
      });
      setDraft(toDraft(detail));
    } catch (err) {
      if (err instanceof HttpError && err.status === 403) {
        setState({ status: "forbidden" });
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to load data sheet";
      setState({ status: "error", message });
    }
  };

  useEffect(() => {
    if (!Number.isFinite(sheetId) || sheetId <= 0) {
      setState({ status: "error", message: "Invalid data sheet id." });
      return;
    }
    void reload();
  }, [sheetId]);

  const isDirty = useMemo(() => {
    if (state.status !== "ready" || !draft) return false;
    return !draftsEqual(draft, toDraft(state.detail));
  }, [state, draft]);

  const hasNameError = draft ? draft.name.trim().length === 0 : false;

  const onSave = async () => {
    if (!draft || saving) return;
    if (draft.name.trim().length === 0) return;
    setSaving(true);
    setSaveError(null);

    // Re-emit columns 1..N in order; empty slots are dropped on the
    // wire and the server pads the rest to NULL. Column type falls
    // back to "text" (1) if the user forgot to pick one.
    const columnsPayload = draft.columns
      .map((c, idx) => ({
        slot: idx + 1,
        name: c.name.trim(),
        typeId: c.typeId || DATA_SHEET_COL_TYPE.text,
      }))
      .filter((c) => c.name.length > 0);

    try {
      const updated = await dataSheetsApi.update(sheetId, {
        name: draft.name.trim(),
        description: draft.description.trim(),
        columns: columnsPayload,
      });
      setState((prev) =>
        prev.status === "ready"
          ? { ...prev, detail: updated }
          : prev,
      );
      setDraft(toDraft(updated));
    } catch (err) {
      const fallback =
        err instanceof Error ? err.message : "Failed to save data sheet";
      const apiDetail =
        err instanceof HttpError && typeof err.data === "object" && err.data
          ? (err.data as { error?: string; detail?: string }).detail ?? null
          : null;
      setSaveError(apiDetail ?? fallback);
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async () => {
    if (state.status !== "ready" || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await dataSheetsApi.remove(sheetId);
      setDeleteConfirmOpen(false);
      navigate("/data");
    } catch (err) {
      const fallback =
        err instanceof Error ? err.message : "Failed to delete data sheet";
      const apiDetail =
        err instanceof HttpError && typeof err.data === "object" && err.data
          ? (err.data as { error?: string; detail?: string }).detail ?? null
          : null;
      setDeleteError(apiDetail ?? fallback);
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  if (!canEditSchema && state.status !== "forbidden") {
    // Hard block in the SPA too — the API also rejects with 403, but
    // blocking client-side avoids showing the editor to users who can't
    // actually save anything.
    return <ForbiddenState onBack={() => navigate("/data")} />;
  }

  const headerTitle =
    state.status === "ready"
      ? state.detail.name || "Data sheet"
      : "Edit data sheet";

  return (
    <>
      <PageHeader
        title={headerTitle}
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigate("/data")}
              disabled={busy}
              style={{
                background: "#ffffff",
                color: "var(--portal-primary)",
                borderColor: "var(--portal-primary)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            {state.status === "ready" && draft && (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setDraft(toDraft(state.detail))}
                  disabled={!isDirty || busy}
                  style={{ background: "#ffffff" }}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onSave}
                  disabled={!isDirty || busy || hasNameError}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Save size={16} />
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && <LoadingState />}
        {state.status === "error" && <ErrorState message={state.message} />}
        {state.status === "forbidden" && <ForbiddenState onBack={() => navigate("/data")} />}

        {state.status === "ready" && draft && (
          <>
            {saveError && (
              <div
                className="tile"
                style={{
                  borderColor: "#e74c3c",
                  color: "#c0392b",
                  background: "#fdecea",
                }}
              >
                <strong>Save failed.</strong>
                <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{saveError}</div>
              </div>
            )}

            <section className="tile">
              <header className="tile-head">
                <h3>Details</h3>
              </header>
              <div style={{ display: "grid", gap: 12 }}>
                <Field label="Name" error={hasNameError ? "Name is required." : null}>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    maxLength={50}
                    disabled={busy}
                    className="portal-input-inline"
                    style={inputStyle()}
                  />
                </Field>
                <Field label="Description">
                  <textarea
                    value={draft.description}
                    onChange={(e) =>
                      setDraft({ ...draft, description: e.target.value })
                    }
                    rows={3}
                    maxLength={250}
                    disabled={busy}
                    className="portal-input-inline"
                    style={{ ...inputStyle(), resize: "vertical" }}
                  />
                </Field>
              </div>
            </section>

            <section className="tile">
              <header className="tile-head">
                <h3>Columns</h3>
              </header>
              <p style={{ color: "var(--portal-muted)", margin: "0 0 12px 0" }}>
                A data sheet has up to {MAX_COLUMNS} columns. Reference
                types (Hunt group, Agent, Voice, Weekplan) show a
                dropdown when editing rows; the other types are
                free-form inputs.
              </p>
              <ColumnsEditor
                columns={draft.columns}
                columnTypes={state.columnTypes}
                disabled={busy}
                onChange={(columns) => setDraft({ ...draft, columns })}
              />
            </section>

            <DangerZone
              name={state.detail.name}
              busy={busy}
              error={deleteError}
              onRequestDelete={() => setDeleteConfirmOpen(true)}
            />
          </>
        )}
      </div>

      {state.status === "ready" && (
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!deleting) setDeleteConfirmOpen(false);
          }}
          title="Delete data sheet?"
          confirmLabel="Delete"
          tone="danger"
          busy={deleting}
          onConfirm={() => void performDelete()}
        >
          Delete data sheet &quot;{state.detail.name}&quot; and all of its
          rows? This cannot be undone.
        </ConfirmModal>
      )}
    </>
  );
}

function ColumnsEditor({
  columns,
  columnTypes,
  disabled,
  onChange,
}: {
  columns: Draft["columns"];
  columnTypes: ColumnType[];
  disabled: boolean;
  onChange: (next: Draft["columns"]) => void;
}) {
  const canAdd = columns.length < MAX_COLUMNS;

  const onNameChange = (idx: number, name: string) => {
    const copy = columns.slice();
    copy[idx] = { ...copy[idx], name };
    onChange(copy);
  };
  const onTypeChange = (idx: number, typeId: number) => {
    const copy = columns.slice();
    copy[idx] = { ...copy[idx], typeId };
    onChange(copy);
  };
  const onRemove = (idx: number) => {
    onChange(columns.filter((_, i) => i !== idx));
  };
  const onAdd = () => {
    if (!canAdd) return;
    onChange([
      ...columns,
      { name: "", typeId: DATA_SHEET_COL_TYPE.text },
    ]);
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {columns.length === 0 && (
        <div
          style={{
            color: "var(--portal-muted)",
            fontSize: "0.9rem",
            padding: "4px 0",
          }}
        >
          No columns yet. Add one to start capturing row data.
        </div>
      )}
      {columns.map((c, idx) => (
        <div
          key={idx}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr 220px auto",
            gap: 10,
            alignItems: "center",
          }}
        >
          <span
            style={{
              width: 28,
              textAlign: "center",
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
            aria-hidden="true"
          >
            {idx + 1}.
          </span>
          <input
            type="text"
            value={c.name}
            onChange={(e) => onNameChange(idx, e.target.value)}
            placeholder="Column name"
            maxLength={50}
            disabled={disabled}
            className="portal-input-inline"
            style={inputStyle()}
            aria-label={`Name of column ${idx + 1}`}
          />
          <select
            value={c.typeId}
            onChange={(e) => onTypeChange(idx, Number(e.target.value))}
            disabled={disabled}
            className="portal-input-inline"
            style={{ ...inputStyle(), cursor: disabled ? "not-allowed" : "pointer" }}
            aria-label={`Type of column ${idx + 1}`}
          >
            {columnTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            onClick={() => onRemove(idx)}
            disabled={disabled}
            title="Remove column"
            style={{ background: "#ffffff" }}
            aria-label={`Remove column ${idx + 1}`}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          className="btn"
          onClick={onAdd}
          disabled={disabled || !canAdd}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={14} /> Add column
          {!canAdd && (
            <span style={{ color: "var(--portal-muted)", marginLeft: 6 }}>
              (max {MAX_COLUMNS})
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

function DangerZone({
  name,
  busy,
  error,
  onRequestDelete,
}: {
  name: string;
  busy: boolean;
  error: string | null;
  onRequestDelete: () => void;
}) {
  return (
    <section className="tile" aria-labelledby="ds-danger">
      <header className="tile-head">
        <h3 id="ds-danger">Delete</h3>
      </header>
      <p style={{ color: "var(--portal-muted)", margin: "0 0 12px 0" }}>
        Deleting a data sheet is permanent. All of its rows will be
        removed and flow-builder nodes that reference it will stop
        resolving values until you point them at a replacement.
      </p>
      {error && (
        <div
          style={{
            marginBottom: 12,
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn"
        onClick={onRequestDelete}
        disabled={busy}
        title={`Delete data sheet "${name}"`}
        style={{
          background: "#ffffff",
          color: busy ? "var(--portal-muted)" : "#c0392b",
          borderColor: busy ? "var(--portal-border)" : "#e74c3c",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Trash2 size={16} />
        Delete data sheet
      </button>
    </section>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span
        style={{
          fontSize: "0.8rem",
          color: "var(--portal-muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      {children}
      {error && <span style={{ fontSize: "0.8rem", color: "#c0392b" }}>{error}</span>}
    </label>
  );
}

function LoadingState() {
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      Loading data sheet…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="tile"
      style={{
        borderColor: "#e74c3c",
        color: "#c0392b",
        background: "#fdecea",
      }}
    >
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{message}</div>
    </div>
  );
}

function ForbiddenState({ onBack }: { onBack: () => void }) {
  return (
    <section className="tile" style={{ maxWidth: 720 }}>
      <h3 style={{ marginTop: 0 }}>You can't edit this schema</h3>
      <p>
        Only admins impersonating this customer can change a data
        sheet's name, description, or column layout. You can still view
        and edit row data from the records page.
      </p>
      <button
        type="button"
        className="btn"
        onClick={onBack}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <ArrowLeft size={16} />
        Back to data sheets
      </button>
    </section>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    padding: "8px 12px",
    border: "1px solid var(--portal-border)",
    borderRadius: 6,
    fontSize: "0.95rem",
    fontFamily: "inherit",
    background: "#ffffff",
  };
}

function toDraft(detail: DataSheetDetail): Draft {
  const filled = detail.columns
    .filter((c) => (c.name ?? "").trim().length > 0)
    .map<Draft["columns"][number]>((c) => ({
      name: c.name,
      typeId: c.typeId || DATA_SHEET_COL_TYPE.text,
    }));
  return {
    name: detail.name ?? "",
    description: detail.description ?? "",
    columns: filled,
  };
}

function draftsEqual(a: Draft, b: Draft): boolean {
  if (a.name !== b.name) return false;
  if (a.description !== b.description) return false;
  if (a.columns.length !== b.columns.length) return false;
  for (let i = 0; i < a.columns.length; i++) {
    if (a.columns[i].name !== b.columns[i].name) return false;
    if (a.columns[i].typeId !== b.columns[i].typeId) return false;
  }
  return true;
}

